// src/pages/Workflows/Editor/hooks/useWorkflowCanvas.js
// Gestão do canvas React Flow: nodes, edges, validação, nó selecionado

import { useState, useCallback } from 'react';
import { useNodesState, useEdgesState, addEdge } from 'reactflow';
import { NODE_DEFINITIONS, paramsIniciaisNo, normalizarConfigNo } from '../utils/nodeDefinitions';
import { isCompativel } from '../utils/typeCompatibility';

let nodeCounter = 0;

function gerarNoId() {
  nodeCounter++;
  return `node_${Date.now()}_${nodeCounter}`;
}

/**
 * Posição por omissão para um nó adicionado sem coordenadas (clique na paleta):
 * alinhado com os nós existentes e abaixo do último — nunca sobreposto nem
 * numa posição aleatória.
 */
function posicaoAbaixoDoUltimo(nodes) {
  if (!nodes.length) return { x: 180, y: 80 };
  const x = Math.min(...nodes.map(n => n.position?.x ?? 180));
  const y = Math.max(...nodes.map(n => n.position?.y ?? 0));
  return { x, y: y + 160 };
}

/**
 * Verifica se adicionar a aresta (source→target) cria um ciclo (DFS).
 */
function temCiclo(nodes, edges, novaAresta) {
  const adj = {};
  nodes.forEach(n => { adj[n.id] = []; });
  [...edges, novaAresta].forEach(e => {
    if (adj[e.source]) adj[e.source].push(e.target);
  });

  const visitado = new Set();
  const emStack = new Set();

  function dfs(id) {
    if (emStack.has(id)) return true;
    if (visitado.has(id)) return false;
    visitado.add(id);
    emStack.add(id);
    for (const vizinho of (adj[id] || [])) {
      if (dfs(vizinho)) return true;
    }
    emStack.delete(id);
    return false;
  }

  return nodes.some(n => dfs(n.id));
}

export function useWorkflowCanvas(definicaoInicial = null) {
  const initialNodes = definicaoInicial?.nodes || [];
  const initialEdges = definicaoInicial?.edges || [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Nó seleccionado (objecto completo)
  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null;

  /**
   * Validação de conexão: tipos compatíveis + sem ciclos.
   */
  const isValidConnection = useCallback((connection) => {
    const { source, target, targetHandle } = connection;

    // Não ligar a si mesmo
    if (source === target) return false;

    // Obter tipo de output do nó fonte
    const noFonte = nodes.find(n => n.id === source);
    if (!noFonte) return false;
    const noTipoFonte = noFonte.data?.nodeType;
    const defFonte = NODE_DEFINITIONS[noTipoFonte];
    if (!defFonte) return false;
    const outputType = defFonte.outputType;

    // Obter tipos aceites pelo campo de input do nó destino
    const noDestino = nodes.find(n => n.id === target);
    if (!noDestino) return false;
    const noTipoDestino = noDestino.data?.nodeType;
    const defDestino = NODE_DEFINITIONS[noTipoDestino];
    if (!defDestino) return false;

    // targetHandle = "input_<nomeCampo>"
    const nomeCampo = targetHandle?.replace('input_', '');

    // Inputs dinâmicos (ex: unificar_valores) — aceita qualquer tipo serializável
    if (defDestino.inputs_dinamicos) {
      const tiposAceites = ['text', 'json_object', 'json_list', 'transcricao_list'];
      if (!isCompativel(outputType, tiposAceites)) return false;
      const entradas = noDestino.data?.config?.params?.entradas || [];
      if (!entradas.some(e => e.input_nome === nomeCampo)) return false;
      const novaAresta = { source, target };
      return !temCiclo(nodes, edges, novaAresta);
    }

    const inputDef = defDestino.inputs?.find(i => i.nome === nomeCampo);
    if (!inputDef) return false;

    if (!isCompativel(outputType, inputDef.tipos_aceites)) return false;

    // Verificar ciclos
    const novaAresta = { source, target };
    if (temCiclo(nodes, edges, novaAresta)) return false;

    return true;
  }, [nodes, edges]);

  const onConnect = useCallback((params) => {
    setEdges(prev => addEdge({ ...params, animated: false }, prev));
  }, [setEdges]);

  /**
   * Adiciona um novo nó ao canvas.
   * `position` vem do drop no canvas; sem ela (clique na paleta) é calculada
   * a partir dos nós já existentes.
   */
  const addNode = useCallback((nodeType, position = null) => {
    const def = NODE_DEFINITIONS[nodeType];
    if (!def || !def.enabled) return;

    const id = gerarNoId();

    setNodes(prev => [...prev, {
      id,
      type: 'workflowNode',
      position: position || posicaoAbaixoDoUltimo(prev),
      data: {
        nodeType,
        config: {
          inputs: {},
          params: paramsIniciaisNo(nodeType),
          campos: nodeType === 'formulario' ? [] : undefined,
        },
      },
    }]);
    setSelectedNodeId(id);
  }, [setNodes]);

  /**
   * Actualiza a config de um nó (painel de configuração).
   */
  const updateNodeConfig = useCallback((nodeId, novaConfig) => {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      return {
        ...n,
        data: {
          ...n.data,
          config: { ...n.data.config, ...novaConfig },
        },
      };
    }));
  }, [setNodes]);

  /**
   * Elimina um nó e todas as arestas ligadas a ele.
   */
  const deleteNode = useCallback((nodeId) => {
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
  }, [setNodes, setEdges, selectedNodeId]);

  /**
   * Inicializa o canvas com uma definição carregada do servidor.
   */
  const inicializarComDefinicao = useCallback((definicao) => {
    setNodes(definicao?.nodes || []);
    setEdges(definicao?.edges || []);
    setSelectedNodeId(null);
  }, [setNodes, setEdges]);

  /**
   * Definição a gravar. Os nós são normalizados para garantir que parâmetros
   * introduzidos depois da criação do workflow (ex: verbosity) ficam sempre
   * explícitos no payload — workflows antigos podem ser regravados sem perder
   * nem inventar configuração.
   */
  const getDefinicao = useCallback(() => ({
    nodes: nodes.map(n => ({
      ...n,
      data: { ...n.data, config: normalizarConfigNo(n.data?.nodeType, n.data?.config || {}) },
    })),
    edges,
  }), [nodes, edges]);

  return {
    nodes, edges,
    onNodesChange, onEdgesChange,
    onConnect,
    isValidConnection,
    selectedNode, setSelectedNodeId,
    addNode,
    updateNodeConfig,
    deleteNode,
    inicializarComDefinicao,
    getDefinicao,
  };
}
